import { FC } from "react";
import config from "config/index";

import classes from "./FollowUs.module.scss";
import {
  INSTAGRAM_URL,
  FACEBOOK_URL,
  LINKEDIN_URL,
  PINTEREST_URL,
  TWITTER_URL,
  YOUTUBE_URL,
  TIKTOK_URL,
} from "constants/configConstants";
import getSiteNavSchema from "@Components/FooterV2/util";
import getImagePath from "@Utils/getImagePath";
import LazyImage from "@Components/Image";
import HelmetServer from "@Components/HelmetServer/index";
import { useSelector, shallowEqual } from "react-redux";
import { AppState } from "@Store/index";
import wrapperHOC from "@Utils/wrapperHOC";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useTranslationHook from "hooks/useTranslationHook";
import useCustomWindowOpen from "@Components/CustomWindowOpen";

interface FollowUsProps {
  isMobile?: boolean;
}

const FollowUs: FC<FollowUsProps> = ({ isMobile = true }) => {
  const customWindowOpen = useCustomWindowOpen();
  const handleRedirect = (link: any) => {
    customWindowOpen({ url: link, target: "_blank", isExternal: true });
  };

  const { t } = useTranslationHook();
  const nonce = useSelector(({ appData }: AppState) => appData.nonce, shallowEqual);
  return !isMobile ? (
    <div className={classes.outerDiv}>
      <div className={classes.label}>{t("home.footer.followus")}:</div>

      <div className={classes.innerDiv}>
        {/* INSTAGRAM */}
        <div className={classes.container} data-testid="footer-instagram">
          <HelmetServer>
            <script type="application/ld+json" suppressHydrationWarning nonce={nonce}>
              {getSiteNavSchema("Instagram", INSTAGRAM_URL)}
            </script>
          </HelmetServer>
          <LazyImage
            src={getImagePath(`${config.IMAGE_STATIC_URL}/images/Instagram.svg`)}
            alt="app store"
            className={classes.mediaImg}
            onClick={() => handleRedirect(INSTAGRAM_URL)}
            width="22px"
            height="22px"
          />
        </div>
        {/* YOUTUBE */}
        <div className={classes.container} data-testid="footer-youtube">
          <HelmetServer>
            <script type="application/ld+json" suppressHydrationWarning nonce={nonce}>
              {getSiteNavSchema("Youtube", YOUTUBE_URL)}
            </script>
          </HelmetServer>
          <LazyImage
            src={getImagePath(`${config.IMAGE_STATIC_URL}/images/Youtube.svg`)}
            alt="youtube"
            className={classes.mediaImg}
            width="22px"
            height="22px"
            onClick={() => handleRedirect(YOUTUBE_URL)}
          />
        </div>
        {/* TIKTOK */}
        <div className={classes.container} data-testid="footer-tiktok">
          <HelmetServer>
            <script type="application/ld+json" suppressHydrationWarning nonce={nonce}>
              {getSiteNavSchema("Tiktok", TIKTOK_URL)}
            </script>
          </HelmetServer>
          <LazyImage
            src={getImagePath(`${config.IMAGE_STATIC_URL}/images/tiktok.svg`)}
            alt="tiktok"
            className={classes.mediaImg}
            width="22px"
            height="22px"
            onClick={() => handleRedirect(TIKTOK_URL)}
          />
        </div>
        {/* LINKEDIN */}
        <div className={classes.container} data-testid="footer-linkedin">
          <HelmetServer>
            <script type="application/ld+json" suppressHydrationWarning nonce={nonce}>
              {getSiteNavSchema("LinkedIn", LINKEDIN_URL)}
            </script>
          </HelmetServer>
          <LazyImage
            src={getImagePath(`${config.IMAGE_STATIC_URL}/images/Linkedin.svg`)}
            alt="app store"
            className={classes.mediaImg}
            width="22px"
            height="22px"
            onClick={() => handleRedirect(LINKEDIN_URL)}
          />
        </div>
        {/* FACEBOOK */}
        <div className={classes.container} data-testid="mobile-footer-facebook">
          <HelmetServer>
            <script type="application/ld+json" suppressHydrationWarning nonce={nonce}>
              {getSiteNavSchema("Facebook", FACEBOOK_URL)}
            </script>
          </HelmetServer>
          <LazyImage
            dataTestId="Facebook"
            src={getImagePath(`${config.IMAGE_STATIC_URL}/images/Facebook.svg`)}
            alt="facebook"
            className={classes.mediaImg}
            width="22px"
            height="22px"
            onClick={() => handleRedirect(FACEBOOK_URL)}
          />
        </div>
        {/* TWITTER */}
        <div className={classes.container} data-testid="footer-twitter">
          <HelmetServer>
            <script type="application/ld+json" suppressHydrationWarning nonce={nonce}>
              {getSiteNavSchema("Twitter", TWITTER_URL)}
            </script>
          </HelmetServer>
          <LazyImage
            dataTestId="Twitter"
            src={getImagePath(`${config.IMAGE_STATIC_URL}/images/Twitter.svg`)}
            alt="twitter icon"
            className={classes.mediaImg}
            width="22px"
            height="22px"
            onClick={() => handleRedirect(TWITTER_URL)}
          />
        </div>
        {/* PINTEREST  */}
        <div className={classes.container} data-testid="footer-pinterest">
          <HelmetServer>
            <script type="application/ld+json" suppressHydrationWarning nonce={nonce}>
              {getSiteNavSchema("Pinterest", PINTEREST_URL)}
            </script>
          </HelmetServer>
          <LazyImage
            src={getImagePath(`${config.IMAGE_STATIC_URL}/images/pinterest.svg`)}
            alt="pinterest icon"
            className={classes.mediaImg}
            width="22px"
            height="22px"
            onClick={() => handleRedirect(PINTEREST_URL)}
          />
        </div>
      </div>
    </div>
  ) : (
    <div className={classes.outerDiv} suppressHydrationWarning>
      <div data-testid="footer-follow-us-on-section" className={classes.label}>
        {t("home.footer.followus")}
      </div>
      <div className={formatClassNames(classes.spacing, classes.innerDiv)}>
        {/* <div className={classes.innerDiv} direction={'row'}  mt={17}> */}
        {/* INSTAGRAM */}
        <div className={classes.container} data-testid="mobile-footer-instagram">
          <HelmetServer>
            <script type="application/ld+json" suppressHydrationWarning nonce={nonce}>
              {getSiteNavSchema("Instagram", INSTAGRAM_URL)}
            </script>
          </HelmetServer>
          <LazyImage
            dataTestId="Instagram"
            src={getImagePath(`${config.IMAGE_STATIC_URL}/images/Instagram.svg`)}
            alt="app store"
            className={classes.mediaImg}
            onClick={() => handleRedirect(INSTAGRAM_URL)}
            width="22px"
            height="22px"
          />
        </div>
        {/* YOUTUBE */}
        <div className={classes.container} data-testid="mobile-footer-youtube">
          <HelmetServer>
            <script type="application/ld+json" suppressHydrationWarning nonce={nonce}>
              {getSiteNavSchema("Youtube", YOUTUBE_URL)}
            </script>
          </HelmetServer>
          <LazyImage
            dataTestId="Youtube"
            src={getImagePath(`${config.IMAGE_STATIC_URL}/images/Youtube.svg`)}
            alt="youtube"
            className={classes.mediaImg}
            width="22px"
            height="22px"
            onClick={() => handleRedirect(YOUTUBE_URL)}
          />
        </div>
        {/* TIKTOK */}
        <div className={classes.container} data-testid="mobile-footer-tiktok">
          <HelmetServer>
            <script type="application/ld+json" suppressHydrationWarning nonce={nonce}>
              {getSiteNavSchema("TikTok", TIKTOK_URL)}
            </script>
          </HelmetServer>
          <LazyImage
            dataTestId="TikTok"
            src={getImagePath(`${config.IMAGE_STATIC_URL}/images/tiktok.svg`)}
            alt="app store"
            className={classes.mediaImg}
            onClick={() => handleRedirect(TIKTOK_URL)}
            width="22px"
            height="22px"
          />
        </div>
        {/* LINKEDIN */}
        <div className={classes.container} data-testid="mobile-footer-linkedin">
          <HelmetServer>
            <script type="application/ld+json" suppressHydrationWarning nonce={nonce}>
              {getSiteNavSchema("LinkedIn", LINKEDIN_URL)}
            </script>
          </HelmetServer>
          <LazyImage
            dataTestId="LinkedIn"
            src={getImagePath(`${config.IMAGE_STATIC_URL}/images/Linkedin.svg`)}
            alt="app store"
            className={classes.mediaImg}
            onClick={() => handleRedirect(LINKEDIN_URL)}
            width="22px"
            height="22px"
          />
        </div>
        {/* FACEBOOK */}
        <div className={classes.container} data-testid="mobile-footer-facebook">
          <HelmetServer>
            <script type="application/ld+json" suppressHydrationWarning nonce={nonce}>
              {getSiteNavSchema("Facebook", FACEBOOK_URL)}
            </script>
          </HelmetServer>
          <LazyImage
            dataTestId="Facebook"
            src={getImagePath(`${config.IMAGE_STATIC_URL}/images/Facebook.svg`)}
            alt="facebook"
            className={classes.mediaImg}
            width="22px"
            height="22px"
            onClick={() => handleRedirect(FACEBOOK_URL)}
          />
        </div>
        {/* TWITTER */}
        <div className={classes.container} data-testid="mobile-footer-twitter">
          <HelmetServer>
            <script type="application/ld+json" suppressHydrationWarning nonce={nonce}>
              {getSiteNavSchema("Twitter", TWITTER_URL)}
            </script>
          </HelmetServer>
          <LazyImage
            dataTestId="Twitter"
            src={getImagePath(`${config.IMAGE_STATIC_URL}/images/Twitter.svg`)}
            alt="twitter icon"
            className={classes.mediaImg}
            width="22px"
            height="22px"
            onClick={() => handleRedirect(TWITTER_URL)}
          />
        </div>
        {/* PINTEREST */}
        <div className={classes.container} data-testid="mobile-footer-pinterest">
          <HelmetServer>
            <script type="application/ld+json" suppressHydrationWarning nonce={nonce}>
              {getSiteNavSchema("Pinterest", PINTEREST_URL)}
            </script>
          </HelmetServer>
          <LazyImage
            dataTestId="Pinterest"
            src={getImagePath(`${config.IMAGE_STATIC_URL}/images/pinterest.svg`)}
            alt="pinterest icon"
            className={classes.mediaImg}
            width="22px"
            height="22px"
            onClick={() => handleRedirect(PINTEREST_URL)}
          />
        </div>
        {/* </div> */}
      </div>
    </div>
  );
};

export default wrapperHOC(FollowUs, {
  componentName: "FollowUs-FooterV2-Global",
});
